import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { getEndpoint } from 'depoto-core'
import { ENDPOINT } from 'depoto-core'
import { useCore } from '../hooks'

interface NetworkStatus {
  isOnline: boolean
  isApiAvailable: boolean
  lastRequestTime: Date | null
  lastRequestStatus: 'success' | 'failure' | null
  lastRequestEndpoint: string | null
  isChecking: boolean
  errorMessage: string | null
}

interface NetInfoContextValue {
  status: NetworkStatus
  reportRequest: (endpoint: string, status: 'success' | 'failure') => void
  checkNetwork: () => Promise<void>
}

const NetInfoContext = createContext<NetInfoContextValue | null>(null)

export const useNetInfo = () => {
  const context = useContext(NetInfoContext)
  if (!context) {
    throw new Error('useNetInfo must be used within NetInfoProvider')
  }
  return context
}

const NETWORK_CHECK_INTERVAL = 30000 // 30 seconds when everything is OK
const NETWORK_CHECK_INTERVAL_ERROR = 5000 // 5 seconds when there are issues
const GOOGLE_GENERATE_204_URL = 'https://clients3.google.com/generate_204'

export const NetInfoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { core } = useCore()
  const checkTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastActivityRef = useRef<Date>(new Date())
  const isCheckingRef = useRef<boolean>(false)

  const [status, setStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    isApiAvailable: true,
    lastRequestTime: null,
    lastRequestStatus: null,
    lastRequestEndpoint: null,
    isChecking: false,
    errorMessage: null,
  })

  const checkGoogleConnectivity = async (): Promise<boolean> => {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout

      const response = await fetch(GOOGLE_GENERATE_204_URL, {
        method: 'HEAD',
        mode: 'no-cors',
        signal: controller.signal,
      })

      clearTimeout(timeoutId)
      // With no-cors, we can't read the response status, but if fetch succeeds, we have connectivity
      return true
    } catch (error) {
      return false
    }
  }

  const checkApiAvailability = async (): Promise<boolean> => {
    try {
      const endpoint = getEndpoint(ENDPOINT.GRAPHQL_POST, core.services.oauth.getSession())
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout

      // Make a simple introspection query
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...core.services.http.getAuthorizationHeaders(),
        },
        body: JSON.stringify({
          query: '{ __typename }',
        }),
        signal: controller.signal,
      })

      clearTimeout(timeoutId)
      return response.ok
    } catch (error) {
      return false
    }
  }

  const checkNetwork = useCallback(async () => {
    // Don't check if already checking
    if (isCheckingRef.current) return

    isCheckingRef.current = true
    setStatus(prev => ({ ...prev, isChecking: true }))

    try {
      // Check Google connectivity first
      const googleAvailable = await checkGoogleConnectivity()

      if (!googleAvailable) {
        // No internet connection
        isCheckingRef.current = false
        setStatus(prev => ({
          ...prev,
          isOnline: false,
          isApiAvailable: false,
          isChecking: false,
          errorMessage: 'No internet connection',
        }))
        return
      }

      // Internet is available, now check our API
      const apiAvailable = await checkApiAvailability()

      console.log({ apiAvailable })

      isCheckingRef.current = false
      setStatus(prev => ({
        ...prev,
        isOnline: true,
        isApiAvailable: apiAvailable,
        isChecking: false,
        errorMessage: apiAvailable ? null : 'API server is unavailable',
      }))
    } catch (error) {
      isCheckingRef.current = false
      setStatus(prev => ({
        ...prev,
        isOnline: false,
        isApiAvailable: false,
        isChecking: false,
        errorMessage: 'Network check failed',
      }))
    }
  }, [core.services.http, core.services.oauth])

  const reportRequest = useCallback((endpoint: string, status: 'success' | 'failure') => {
    const now = new Date()
    lastActivityRef.current = now

    setStatus(prev => ({
      ...prev,
      lastRequestTime: now,
      lastRequestStatus: status,
      lastRequestEndpoint: endpoint,
      // Update connectivity status based on request result
      isOnline: status === 'success' ? true : prev.isOnline,
      isApiAvailable: status === 'success' ? true : prev.isApiAvailable,
      errorMessage: status === 'failure' ? 'Last request failed' : null,
    }))
  }, [])

  // Listen to browser online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setStatus(prev => ({ ...prev, isOnline: true }))
      checkNetwork()
    }

    const handleOffline = () => {
      setStatus(prev => ({
        ...prev,
        isOnline: false,
        isApiAvailable: false,
        errorMessage: 'No internet connection',
      }))
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [checkNetwork])

  // Periodic network check if no activity
  useEffect(() => {
    // Determine the check interval based on current status
    const hasIssues = !status.isOnline || !status.isApiAvailable || status.lastRequestStatus === 'failure'
    const checkInterval = hasIssues ? NETWORK_CHECK_INTERVAL_ERROR : NETWORK_CHECK_INTERVAL

    const intervalId = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current.getTime()
      // If no activity for the specified interval, perform artificial check
      // Use shorter interval when there are issues for faster recovery detection
      if (timeSinceLastActivity > checkInterval) {
        checkNetwork()
      }
    }, checkInterval)

    // Initial check
    checkNetwork()

    return () => {
      clearInterval(intervalId)
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current)
      }
    }
  }, [checkNetwork, status.isOnline, status.isApiAvailable, status.lastRequestStatus])

  const value: NetInfoContextValue = {
    status,
    reportRequest,
    checkNetwork,
  }

  return <NetInfoContext.Provider value={value}>{children}</NetInfoContext.Provider>
}
